import React, { useEffect, useReducer, useRef, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { AgendaStreamingContext } from '../../common/AgendaStreamingContextProvider';
import CountrySelector from '../../common/CountrySelector';
import ExpandCollapse from '../../common/ExpandCollapse';
import { FavoritesContext } from '../../common/FavoritesContextProvider';
import FavouritesList from '../../common/FavouritesList';
import FavouritesSelection from '../../common/FavouritesSelection';
import IconsScroller from '../../common/IconsScroller';
import Loader from '../../common/Loader';
import MatchesList from '../../common/MatchesList';
import MessageInfo from '../../common/MessageInfo';
import SportSelection from '../../common/SportSelection';
import Tournament from '../../common/Tournament';
import LiveContext from '../../contexts/LiveContext';
import { useFetchMenuLiveGeneraleNew } from '../../data/fetchMenuLiveGeneraleNew';
import { getBetgeniusStreamingProps, getBetradarStreamingProps, getBetgeniusV3StreamingProps, getArenaStreamingProps, getIgameMediaStreamingProps } from '../../data/streaming/streamingUtils';
import { classIf, filterSearchResults, getConfiguration, iconSportCssClass } from '../../data/utils';
import { appConfig } from '../../InitData';
import { getLabel } from '../../data/labels';
import { useScrollableData } from '../../scrollableData';
import { initLiveSectionEffects } from '../../sport-effects';
import AgendaStreaming from './agenda-streaming/AgendaStreaming';
import SeoMainContainer from '../../data/seo/SeoMainContainer';
import SeoMetaTags from '../../data/seo/SeoMetaTags';
import { useFetchSeoSettings } from '../../data/fetchSeoSettings';
import { useHandleSeoSettings } from '../../data/seo/seoUtils';
import { SeoContext } from '../../data/seo/SeoContextProvider';
import MainContext from '../../contexts/MainContext';
import { concatUrlPathnames } from '../../data/sport-utils';
import AgendaStreamingSelection from './agenda-streaming/AgendaStreamingSelection';
import { systemCode } from '../..';

export const LiveSectionContext = React.createContext(null);

function getSortType() {
    let sortType = 'league';
    const item = localStorage.getItem('xs_live_sort_type');
    if (item) {
        sortType = item;
    }
    return sortType;
}

function reducer(state, action) {
    switch (action.type) {
        case 'setData':
            const data = action.payload;
            let sport = data.sport.find(s => s.id === state.sportFetch);
            if (sport === undefined) {
                sport = data.sport[0];
            }
            if (sport == null) {
                return {
                    ...state,
                    noMatches: true
                }
            } else if (sport.categories.length === 0) {
                return {
                    ...state,
                    sport: sport,
                    noMatches: true
                }
            } else {
                const country = sport.categoryById[state.country?.id] ?? sport.categories[0];
                const tournament = country.tournamentById[state.tournament?.id] ?? country.tournaments[0];
                const match = tournament.matchById[state.match?.id] ?? tournament.events[0];
                return {
                    ...state,
                    data,
                    sport,
                    sportsCount: data.sport.length,
                    countries: data.sportById[sport.id].categories,
                    country: country,
                    tournaments: country.tournaments,
                    tournament,
                    match,
                    matchId: match?.uniqueId,
                    noMatches: false,
                };
            }
        case 'changeSport':
            return {
                ...state,
                sportFetch: action.payload,
            };
        case 'changeCountry':
            const countryId = action.payload;
            const country = state.sport.categoryById[countryId]
            const tournaments = country.tournaments;
            const tournament = tournaments[0];
            const match = tournament.events[0];
            return {
                ...state,
                country,
                tournaments,
                tournament,
                match,
                matchId: match?.uniqueId,
                collapsedTournament: false
            };
        case 'changeMatch':
            const t = state.country.tournamentById[action.payload.tournamentId];
            const m = t.matchById[action.payload.matchId];
            return {
                ...state,
                tournament: t,
                match: m,
                matchId: m.uniqueId
            };
        case 'setSortType':
            if (action.payload !== state.sortType) {
                localStorage.setItem('xs_live_sort_type', action.payload);
                return {
                    ...state,
                    sortType: action.payload
                }
            }
            break;
        default:
            throw new Error();
    }
}

function LiveSection(props) {
    const conf = getConfiguration();
    const cfgSeo = conf.seo;
    const isStreamingEnabled = getBetgeniusStreamingProps() || getBetgeniusV3StreamingProps() ||getBetradarStreamingProps() || getArenaStreamingProps() || getIgameMediaStreamingProps();
    const mainContext = React.useContext(MainContext);
    const liveContext = React.useContext(LiveContext);
    const favoritesContext = React.useContext(FavoritesContext);
    const favoritesToggled = favoritesContext.showFavourites;
    const favorites = favoritesContext.favorites;
    const favCount = Object.keys(favorites).length;
    const agendaStreamingContext = React.useContext(AgendaStreamingContext);
    const seoContext = React.useContext(SeoContext);
    const [seoSettingsState] = useFetchSeoSettings(seoContext.pathname, false, cfgSeo && (mainContext.currentSection.current === 'live-suite' || mainContext.currentSection.current === 'multi-bet'));

    useHandleSeoSettings(seoSettingsState);

    const [state, dispatch] = useReducer(reducer, {
        data: null,
        sportFetch: parseInt(props.openFromPath?.sport?.sportId) || 0,
        sport: parseInt(props.openFromPath?.sport?.sportId) || 0,
        sportsCount: 0,
        countries: null,
        country: null,
        tournaments: null,
        tournament: null,
        match: null,
        matchId: null,
        noMatches: false,
        sortType: getSortType(),
    });
    const [collapsedTournament, setCollapsedTournament] = useState(false);
    const [sortMenuToggled, setSortMenuToggled] = useState(false);

    function changeSport(sportId) {
        if (sportId !== state.sport.id) {
            setSearch('');
            dispatch({type: 'changeSport', payload: sportId});
        }
        favoritesContext.setShowFavourites(false);
        agendaStreamingContext.setShowAgendaStreaming(false);
    }
    
    function changeCountry(countryId) {
        setSearch('');
        dispatch({type: 'changeCountry', payload: countryId});
    }

    function changeMatch(match) {
        const handlePrematchLiveEventSameUrl = conf.seoplesu;
        if (!favoritesToggled) {
            dispatch({type: 'changeMatch', payload: {tournamentId: match.tournamentRef, matchId: match.id}});
        }
        if (props.isLiveSuite) {
            props.openMatch(match);
        } else if(handlePrematchLiveEventSameUrl) {
            liveContext.setMatchDataPalAvv({
                sportId: match.sportRef,
                countryId: match.categoryRef,
                tournamentId: match.tournamentRef,
                pal: match.pal,
                avv: match.avv
            }, true);
            let callback = () =>{
                setTimeout(() => {
                    const path = concatUrlPathnames(appConfig.ROUTES['prematch']['top-match'],match.urlPathSeo);
                    window.history.replaceState(window.history.state, null, path);
                    seoContext.refreshPathname(match.urlPath, mainContext.currentSection.current);
                }, 5)
            } 
            window.openSection('live-match',callback);
        }else{
            props.openMatch(match.sportRef, match.categoryRef, match.tournamentRef, match.id);
        }
    }

    function collapseTournaments(){
        setCollapsedTournament(true);
    }

    function expandTournaments(){
        setCollapsedTournament(false);
    }

    function changeSortType(sortType) {
        if(state.sortType != sortType){
            dispatch({type: 'setSortType', payload: sortType});
        }
    }

    // useEffect(() => {
    //     if(props.openMatchOnChange&&props.hideSelectedMatch){
    //         props.hideSelectedMatch();
    //     }
    // }, [sportId]);

    const firstRender = useRef(true);
    if (firstRender.current) {
        firstRender.current = false;
        initLiveSectionEffects();
    }

    const toggleAgendaStreaming = agendaStreamingContext.showAgendaStreaming;
    const [request, , isSetInterval] =  useFetchMenuLiveGeneraleNew(state.sportFetch, null, function(parsedData) {
        if (parsedData) {
            dispatch({type: 'setData', payload: parsedData})
            liveContext.setData(parsedData);
        } else {
            console.error("no data available from server");
        }
    },false,!toggleAgendaStreaming);

    const [showLoader, setShowLoader] = useState(true);
    useEffect(function() {
        let timeout = null;
        if (request.isLoading) {
            timeout = setTimeout(function() {
                setShowLoader(true);
            }, conf.lspt);
        } else {
            clearTimeout(timeout);
            setShowLoader(false);
            if (!request.isError) {
                if (request.data) {
                    dispatch({type: 'setData', payload: request.data})
                    liveContext.setData(request.data);
                } else {
                    console.error("No data available from server");
                }
                if (props.openMatchOnChange && props.showSelectedMatch) {
                    props.showSelectedMatch();
                }
            }
        }

        return () => {
            clearInterval(timeout);
        };
    }, [request]);

    
    useEffect(() => {
        if(favoritesToggled) {
            agendaStreamingContext.setShowAgendaStreaming(false);
            setSearch(null);
        }
    }, [favoritesContext]); 
    
    useEffect(() => {
        if (agendaStreamingContext.showAgendaStreaming) {
            favoritesContext.setShowFavourites(false);
        }
    }, [agendaStreamingContext]);

    const [search, setSearch] = useState(null);

    function handleChange(event) {
        setSearch(event.target.value);
    }

    useEffect(() => {
        if (props.openMatchOnChange && !favoritesToggled && state.match != null) {
            props.openMatch(state.match);
        }
    }, [state.matchId]);

    const iconsContainer = useRef(null);
    
    useEffect(() => {
        if (state.tournaments) {
            scrollableData.resetData(state.tournaments, scrollableData.scrolledItems.prev > 0 ? scrollableData.scrolledItems.next : 5);
        }
    }, [state.tournaments]);
    const [scrollableData] = useScrollableData(state.tournaments, 5, 1);
    const infiniteRef = useInfiniteScroll({
        loading: scrollableData.loading,
        hasNextPage: scrollableData.hasMoreItems,
        onLoadMore: scrollableData.getMoreData,
        scrollContainer: scrollableData.container
    });

    if ((!isSetInterval && showLoader) || request.isError) {
        return <Loader error={request.isError} />;
    }

    if (state.data == null) {
        return (
            <>
                 <SeoMetaTags />
                 <MessageInfo message={`${getLabel('general.eventi_live')} ${getLabel('general.chiuso')}`} />
            </>
        );
    }

    const sportButtons = state.data.sport.map((sport, index) => {
        return (
            <React.Fragment key={sport.id}>
                <SportSelection key={sport.id} {...sport} order={index+1} counter={sport.eventsCount} selected={!favoritesToggled && !toggleAgendaStreaming && sport.id === state.sport.id} changeSport={changeSport} />
                {isStreamingEnabled && index===state.data.sport.length-1 &&
                    <AgendaStreamingSelection key={'agenda-streaming-selection'} order={state.data.sport?state.data.sport.length+1:null} selected={toggleAgendaStreaming}  />
                }
            </React.Fragment>
        );
    });

    let filteredTournaments = scrollableData.enabled ? scrollableData.visibleItems : state.tournaments;
    if (state.tournaments != null && search != null) {
        filteredTournaments = [];
        state.tournaments.forEach(function(tournament) {
            const filteredMatches = filterSearchResults(tournament.events, search, 'MATCH', function(match) {
                return match.desc;
            });
    
            if (filteredMatches.length > 0) {
                filteredTournaments.push({
                    ...tournament,
                    events: filteredMatches
                });
            }
        });
    }
    const tournamentsList = filteredTournaments?.map(function(tournament) {
        return (
            <Tournament key={tournament.id}
                        {...tournament}
                        changeMatch={changeMatch}
                        currentMatch={state.match}
                        collapse={collapsedTournament}
                        isLiveSuite={props.isLiveSuite}
                        moreBetsEnabled={props.moreBetsEnabled}
            />
        );
    });
    
    const isPrematchPage = appConfig.ROUTES.sectionFromPath(window.location.pathname,appConfig.ROUTES['prematch']);

      function openPrematchSection(e) {
        e.preventDefault();
        e.stopPropagation();
        window.openSection('prematch');
    }
    
    return (
        <>
            <SeoMetaTags />
            <LiveSectionContext.Provider value={state}>
                <div className="sport-live-suite" id="calcio">
                    <div className="container-slider-selezione-sport">
                        <div className="container-slider-icone mb-2 overflow-unset has-icons-container-no-wrap">
                            <div className="icons-slider-container">
                            {systemCode === 'STARCASINO' && <div className='icon-slider' onClick={(e) => openPrematchSection(e)}><li className='icon-slider-home'></li></div>}
                                <div className="slider-icone-sport horiz-sc icons-container icons-container-no-wrap" ref={iconsContainer}>
                                    <FavouritesSelection key={'favourites-selection'} order={0} selected={favoritesToggled} counter={favCount} />
                                    {sportButtons}
                                </div>
                                <IconsScroller buttons={sportButtons} iconsContainer={iconsContainer.current} />
                            </div>
                            {!favoritesToggled && !toggleAgendaStreaming &&
                                <div className="sort-button position-relative" onClick={() => {setSortMenuToggled(!sortMenuToggled)}}>
                                    {sortMenuToggled &&
                                        <div className="nav-slider-menu">
                                            <div className="nav-slider-menu-close-button push-right pointer" onClick={() => {setSortMenuToggled(!sortMenuToggled)}}>
                                                <i className="fa fa-times"></i>
                                            </div>
                                            <div className={"nav-slider-menu-row pointer" + classIf(state.sortType === 'league', 'selected')} onClick={() => {changeSortType('league')}}>
                                                <i className="fa fa-sort-alpha-down"></i>
                                                <span>{getLabel('sort_menu.sort_by_tournament')}</span>
                                            </div>
                                            <div className={"nav-slider-menu-row pointer" + classIf(state.sortType === 'desc', 'selected')} onClick={() => {changeSortType('desc')}}>
                                                <i className="fa fa-sort-down"></i>
                                                <span>{getLabel('sort_menu.sort_by_chronological_order')}</span>
                                            </div>
                                            <div className={"nav-slider-menu-row pointer" + classIf(state.sortType === 'asc', 'selected')} onClick={() => {changeSortType('asc')}}>
                                                <i className="fa fa-sort-up"></i>
                                                <span>{getLabel('sort_menu.sort_by_reverse_chronological_order')}</span>
                                            </div>
                                        </div>
                                    }
                                    <i className="fa fa-sort-alpha-down"></i>
                                    <span>{getLabel('sort_menu.title')}</span>
                                </div>
                            }
                        </div>
                        {!(favoritesToggled || toggleAgendaStreaming) &&
                            <div className="bg-nero bianco titolo-blocco-sport pointer">
                                <span className="d-centro-v maiuscolo">
                                    <div className={`img-fluid icona-sport margine-dx-mezzo ico-sport ${iconSportCssClass(state.sport.id, state.data.sport.iconClass)}`}></div>
                                    {state.sport.descrizione}
                                </span>
                                <div className="d-flex justify-content-center align-items-center">
                                    <ExpandCollapse key={`expand-collapse-${state.sportId}_${state.currentCountry}`} querySelectorAll={'.sport-live-suite .contenitore-table'} classNames={['d-centro-v mr-3','bianco pl-2 pointer','voce-riduci bianco']} startCollapsed={collapsedTournament}  onCollapse={collapseTournaments} onExpand={expandTournaments} />
                                    <div className="ricerca-titolo-blocco-sport">
                                        <input type="text" placeholder={getLabel('general.search_match')} name="search" onChange={handleChange} value={search||undefined} autoFocus autoComplete="off" />
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                    {favoritesToggled ?
                        <FavouritesList search={search} changeMatch={changeMatch} currentMatch={state.match.id} isLiveSuite={props.isLiveSuite} />
                        :
                        toggleAgendaStreaming ?
                            <AgendaStreaming />
                            :
                            <div className="collapse bg-bianco margine-giu-mezzo show bordo-tondo" id="tabella-aa">
                                {state.noMatches ? 
                                    <MessageInfo message={getLabel('live_page.streams_schedule.no_matches')} />
                                    :
                                    <>
                                        <CountrySelector countryList={state.countries} selected={state.country.id} changeCountry={changeCountry} />
                                        <div ref={scrollableData.enabled ? infiniteRef : null} >
                                            {state.sortType === 'league' ?
                                                tournamentsList
                                                :
                                                <MatchesList tournaments={filteredTournaments} categoryDesc={state.country.descrizione} changeMatch={changeMatch} collapse={collapsedTournament} isLiveSuite={props.isLiveSuite} />
                                            }
                                        </div>
                                    </>
                                }
                            </div>
                    }
                </div>
                {!isPrematchPage && (!scrollableData.enabled || (!scrollableData.hasMoreItems || !request.isSetInterval)) && (props.isLiveSuite || (appConfig?.ROUTES?.['live']?.['live-suite'] !=  appConfig?.ROUTES?.['live']?.['multi-bet']) || conf.iwh  ) && 
                    <SeoMainContainer />
                }
            </LiveSectionContext.Provider>
        </>
    );
}

export default LiveSection;